import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import Button from "./primitives/Button";
import Dropdown from "./primitives/Dropdown";
import { validRegions } from "../utils/validRegions";

const RegionSelectionModal = ({
  open,
  onRegionSelect,
  loading,
  error,
  onClose,
  isUpdate = false,
}) => {
  const [selectedRegion, setSelectedRegion] = useState("");

  const handleRegionChange = (e) => {
    setSelectedRegion(e.target.value);
  };

  const handleSubmit = () => {
    if (selectedRegion) {
      onRegionSelect(selectedRegion);
    }
  };

  const handleClose = (_event, reason) => {
    if (!isUpdate && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      // Prevent closing during first-time onboarding
      return;
    }
    if (isUpdate && onClose) {
      onClose();
    }
  };

  const regionOptions = validRegions.map((region) => ({
    value: region,
    label: region,
  }));

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown={!isUpdate}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2, p: 1 } } }}
      onClose={handleClose}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? "Change Your Region" : "Select Your Region"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
          {isUpdate
            ? "Select a new region below. This will take effect immediately and update your available configurations."
            : "To get started, please select your region. This will determine which configurations are available to you."}
        </Typography>
        {!isUpdate && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Once you select a region, it cannot be changed without
              contacting the development team.
            </Typography>
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Dropdown
            label={isUpdate ? "New Region" : "Select Region"}
            id="region-select"
            name="region"
            value={selectedRegion}
            onChange={handleRegionChange}
            options={[{ value: "", label: "Choose a region..." }, ...regionOptions]}
            disabled={loading}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
        {isUpdate && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={loading}
            name="Cancel"
          />
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!selectedRegion || loading}
          endIcon={loading ? <CircularProgress size={16} /> : null}
          name={loading ? "Updating..." : isUpdate ? "Update Region" : "Continue"}
          fullWidth={!isUpdate}
        />
      </DialogActions>
    </Dialog>
  );
};

export default RegionSelectionModal;
